import type { App } from '../app/App';
import { FORTUNE_COLORS } from '../game/data/fortune';
import { h } from './dom';

/**
 * The paper fortune teller, seen from above: four pockets (quarters) that
 * open up/down or sideways. Delivered art (fortune:teller / fortune:inside /
 * fortune:flap_lift, art batch 10) is swapped in per pose; until then it is
 * drawn in SVG like the real toy: four cream paper pyramids with a coloured
 * heart each, tilted towards the player, that snap shut and open up/down or
 * sideways (CSS), showing the dark pocket inside.
 *
 * Quarters are numbered 0 top-left, 1 top-right, 2 bottom-left, 3 bottom-right.
 */
export type TellerPose = 'closed' | 'open_a' | 'open_b';

export interface TellerView {
  el: HTMLElement;
  setPose(pose: TellerPose): void;
  /** Outside view: the coloured pockets with a word on each. */
  showOutside(labels: string[]): void;
  /** Inside view: white flaps with a number on each. */
  showInside(numbers: number[]): void;
  /** Spot buttons: enable them and say what a tap does (null = not tappable). */
  setPick(onPick: ((quarter: number) => void) | null): void;
  /** Fold the chosen inside flap back. */
  lift(quarter: number): void;
}

const SVG_NS = 'http://www.w3.org/2000/svg';
/** Each quarter's corner on the 240×240 drawing and its outer corner. */
const QUARTERS = [
  { x: 10, y: 10, ox: 10, oy: 10 },
  { x: 120, y: 10, ox: 230, oy: 10 },
  { x: 10, y: 120, ox: 10, oy: 230 },
  { x: 120, y: 120, ox: 230, oy: 230 },
];

/** Heart ink per colour pocket (stronger than the pocket fill, like a felt-tip heart). */
const HEART: Record<string, string> = { blue: '#2f6fd0', pink: '#e4407a', green: '#2fae6b', yellow: '#f2b900' };
const PAPER = '#f6efd8';

/** One closed pocket seen from above: a paper pyramid whose four faces catch the light differently. */
function pyramid(i: number): string {
  const q = QUARTERS[i]!;
  const { x, y } = q;
  const cx = x + 55;
  const cy = y + 55;
  const faces: [string, string, number][] = [
    [`${x},${y} ${x + 110},${y} ${cx},${cy}`, '#ffffff', 0.55], // top: lit
    [`${x + 110},${y} ${x + 110},${y + 110} ${cx},${cy}`, '#000000', 0.16],
    [`${x},${y + 110} ${x + 110},${y + 110} ${cx},${cy}`, '#000000', 0.32], // bottom: in shade
    [`${x},${y} ${x},${y + 110} ${cx},${cy}`, '#ffffff', 0.25],
  ];
  // The heart sits on the outer slope, near the outer corner, clear of the colour word on the tip.
  const hx = cx + (q.ox - cx) * 0.62 - 13;
  const hy = cy + (q.oy - cy) * 0.62 - 12;
  const ink = HEART[FORTUNE_COLORS[i]!.id] ?? '#e4407a';
  return `<g class="ft-q ft-q${i}">
    <rect x="${x}" y="${y}" width="110" height="110" rx="3" fill="${PAPER}" stroke="none"/>
    ${faces.map(([pts, c, o]) => `<polygon points="${pts}" fill="${c}" fill-opacity="${o}" stroke="none"/>`).join('')}
    <path d="M${x} ${y}L${x + 110} ${y + 110}M${x + 110} ${y}L${x} ${y + 110}" stroke-width="1.4" opacity=".45"/>
    <path transform="translate(${hx} ${hy})" d="M13 23C5 17 0 12 0 7C0 3 3 0 7 0C10 0 12 2 13 4C14 2 16 0 19 0C23 0 26 3 26 7C26 12 21 17 13 23Z" fill="${ink}" stroke-width="1.6"/>
    <rect x="${x}" y="${y}" width="110" height="110" rx="3" fill="none"/>
  </g>`;
}

function drawSvg(view: 'outside' | 'inside'): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 240 240');
  svg.setAttribute('class', 'ft-svg');
  svg.setAttribute('aria-hidden', 'true');
  if (view === 'outside') {
    // Behind the pockets: the dark inside with its folded flaps, seen when the teller opens.
    svg.innerHTML = `<g fill="none" stroke="#3b3b3b" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" filter="url(#wobble)">
      <g class="ft-mouth">
        <path d="M120 22L218 120L120 218L22 120Z" fill="#4a3b2f" stroke="none"/>
        <path d="M120 120L120 40M120 120L200 120M120 120L120 200M120 120L40 120" stroke="#e9dfc4" stroke-width="2" opacity=".7"/>
        <path d="M120 120L64 64M120 120L176 64M120 120L176 176M120 120L64 176" stroke="#e9dfc4" stroke-width="1.2" opacity=".35"/>
      </g>
      ${[0, 1, 2, 3].map(pyramid).join('')}
    </g>`;
    return svg;
  }
  const quarters = QUARTERS.map((q, i) => {
    const fill = '#fbf8ee';
    // Inside: each flap is creased from its outer corner to the centre.
    const creases = `<path d="M${q.ox} ${q.oy}L120 120" stroke-width="1.6" opacity=".55"/>`;
    return `<g class="ft-q ft-q${i}"><rect x="${q.x}" y="${q.y}" width="110" height="110" rx="4" fill="${fill}"/><rect x="${q.x}" y="${q.y}" width="110" height="110" rx="4" fill="url(#ft-hatch)" stroke="none"/>${creases}<rect x="${q.x}" y="${q.y}" width="110" height="110" rx="4" fill="none"/></g>`;
  });
  svg.innerHTML = `<defs><pattern id="ft-hatch" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(-30)"><path d="M0 0V7" stroke="#3b3b3b" stroke-width="1.2" opacity=".12"/></pattern></defs>
    <g fill="none" stroke="#3b3b3b" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" filter="url(#wobble)">
      <path class="ft-mouth" d="M120 30L210 120L120 210L30 120Z" fill="#6b5a4a" stroke="none"/>
      ${quarters.join('')}
    </g>`;
  return svg;
}

/** Time the toy stays shut between two opens, like fingers pinching it closed. */
const SNAP_MS = 90;

export function fortuneTeller(app: App): TellerView {
  const art = h('img', { class: 'art-img ft-art', alt: '', draggable: false, 'aria-hidden': 'true' });
  const lifted = h('img', { class: 'art-img ft-lift-art', alt: '', draggable: false, 'aria-hidden': 'true' });
  const spots = QUARTERS.map((_, i) => h('button', { class: `ft-spot ft-spot${i}`, type: 'button', disabled: true }));
  const drawing = h('div', { class: 'ft-drawing' });
  const el = h('div', { class: 'ft-toy pose-closed view-outside' }, drawing, ...spots);
  let view: 'outside' | 'inside' = 'outside';
  let pose: TellerPose = 'closed';
  let pick: ((q: number) => void) | null = null;
  let snap = 0;
  spots.forEach((b, i) => b.addEventListener('click', () => pick?.(i)));

  const render = (): void => {
    const url = view === 'outside' ? app.assets.url('fortune:teller', pose) : app.assets.url('fortune:inside');
    if (url) {
      art.src = url;
      if (art.parentElement !== drawing) drawing.replaceChildren(art);
    } else drawing.replaceChildren(drawSvg(view));
  };
  const label = (texts: (string | number)[]): void => spots.forEach((b, i) => (b.textContent = String(texts[i] ?? '')));

  render();
  return {
    el,
    setPose(next) {
      const apply = (p: TellerPose): void => {
        el.classList.replace(`pose-${pose}`, `pose-${p}`);
        pose = p;
        if (view === 'outside' && app.assets.url('fortune:teller', pose)) render();
      };
      window.clearTimeout(snap);
      // Opening again: pinch it shut first, then open the other way, so every count is a real open.
      if (next !== 'closed' && pose !== 'closed') {
        apply('closed');
        snap = window.setTimeout(() => apply(next), SNAP_MS);
      } else apply(next);
    },
    showOutside(labels) {
      view = 'outside';
      el.classList.replace('view-inside', 'view-outside');
      el.querySelector('.ft-q.lifted')?.classList.remove('lifted');
      lifted.remove();
      label(labels);
      render();
    },
    showInside(numbers) {
      view = 'inside';
      el.classList.replace('view-outside', 'view-inside');
      label(numbers);
      render();
    },
    setPick(onPick) {
      pick = onPick;
      for (const b of spots) b.disabled = !onPick;
      // A little wiggle while it waits for a choice.
      el.classList.toggle('waiting', Boolean(onPick));
    },
    lift(quarter) {
      spots[quarter]?.classList.add('picked');
      const url = app.assets.url('fortune:flap_lift');
      if (url) {
        lifted.src = url;
        lifted.className = `art-img ft-lift-art ft-lift${quarter}`;
        el.appendChild(lifted);
      } else el.querySelector(`.ft-q${quarter}`)?.classList.add('lifted');
    },
  };
}
